import { coordinate, type Coordinate, type Player } from "./model";

export const BOARD_SIZE = 5;
export const CELL_COUNT = BOARD_SIZE * BOARD_SIZE;
export const MAX_STACK_HEIGHT = 3;

export const ADJACENT_OFFSETS: readonly Coordinate[] = Object.freeze(
  [-1, 0, 1].flatMap((row) =>
    [-1, 0, 1]
      .filter((column) => row !== 0 || column !== 0)
      .map((column) => coordinate(row, column)),
  ),
);

export function isIntegerCoordinate(value: unknown): value is Coordinate {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return Number.isInteger(candidate["row"]) && Number.isInteger(candidate["column"]);
}

export function isOnBoard(position: Coordinate): boolean {
  return (
    position.row >= 0 &&
    position.row < BOARD_SIZE &&
    position.column >= 0 &&
    position.column < BOARD_SIZE
  );
}

export function cellIndex(position: Coordinate): number {
  if (!isOnBoard(position)) {
    throw new RangeError(`Coordinate (${position.row}, ${position.column}) is outside the board.`);
  }
  return position.row * BOARD_SIZE + position.column;
}

export function coordinateAt(index: number): Coordinate {
  if (!Number.isInteger(index) || index < 0 || index >= CELL_COUNT) {
    throw new RangeError(`Cell index ${index} is outside the board.`);
  }
  return coordinate(Math.floor(index / BOARD_SIZE), index % BOARD_SIZE);
}

export function isAdjacent(from: Coordinate, to: Coordinate): boolean {
  const rowDistance = Math.abs(to.row - from.row);
  const columnDistance = Math.abs(to.column - from.column);
  return rowDistance <= 1 && columnDistance <= 1 && rowDistance + columnDistance > 0;
}

export function forwardRowDelta(player: Player): -1 | 1 {
  return player === "black" ? -1 : 1;
}

export function progressAt(player: Player, position: Coordinate): number {
  return player === "black" ? BOARD_SIZE - 1 - position.row : position.row;
}

export function compareCoordinates(left: Coordinate, right: Coordinate): number {
  return left.row - right.row || left.column - right.column;
}
